/**
 * Composant de paramètres de sécurité
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Vue complète des paramètres de sécurité avec logs, alertes et statistiques.
 */

  // Client Component pour Next.js

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSecurity } from '../../hooks';
import { useSecurityAlert } from '../ui/SecurityAlert';
import { SecurityAlert } from '../ui/SecurityAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { SecurityLog, LoginAttempt, SecuritySettings as SecuritySettingsType } from '../../../application/dto/dtos';

/**
 * Type pour les activités suspectes
 */
interface SuspiciousActivity {
  description: string;
  dateDetection: string;
}

/**
 * Props du composant SecuritySettings
 */
export interface SecuritySettingsProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Afficher la section 2FA */
  show2FASection?: boolean;
  /** Callback pour naviguer vers la configuration 2FA */
  onConfigure2FA?: () => void;
  /** Afficher la section changement de mot de passe */
  showPasswordSection?: boolean;
  /** Callback pour naviguer vers le changement de mot de passe */
  onChangePassword?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant SecuritySettings
 *
 * @example
 * ```tsx
 * function SecurityPage() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <SecuritySettings
 *       userId={user.id}
 *       onConfigure2FA={() => navigate('/settings/2fa')}
 *       onChangePassword={() => navigate('/settings/password')}
 *     />
 *   );
 * }
 * ```
 */
export function SecuritySettings({
  userId,
  show2FASection = true,
  onConfigure2FA,
  showPasswordSection = true,
  onChangePassword,
  className = '',
}: SecuritySettingsProps) {
  const {
    isLoading,
    error,
    getSecuritySettings,
    updateSecuritySettings,
    blockIP,
    getSecurityLogs,
    getLoginAttempts,
    clearError

  } = useSecurity();

  const { alert, showAlert, hideAlert } = useSecurityAlert();

  // Etats pour les donnees de securite
  const [securityLogs, setSecurityLogs] = useState<SecurityLog[]>([]);
  const [suspiciousActivities, setSuspiciousActivities] = useState<SuspiciousActivity[]>([]);
  const [ipAutorisees, setIpAutorisees] = useState<string[]>([]);
  const [settings, setSettings] = useState<SecuritySettingsType | null>(null);
  const [securityParams, setSecurityParams] = useState<SecuritySettingsType | null>(null);

  // Etats pour l'UI
  const [showLogs, setShowLogs] = useState(false);
  const [showBlacklist, setShowBlacklist] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [ipError, setIpError] = useState<string | null>(null);
  /**
   * Charger les donnees de securite au montage
   */
  useEffect(() => {
    loadSecurityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /**
   * Charger toutes les donnees de securite
   */
  const loadSecurityData = async () => {
    try {
      // Charger les parametres de securite
      const settingsData = await getSecuritySettings();
      if (settingsData) {
        setSettings(settingsData);
        setSecurityParams(settingsData);
        setIpAutorisees(settingsData.ipAutorisees || []);
      }

      // Charger les logs
      await fetchSecurityLogs();

      // Charger les tentatives de connexion pour detecter les activites suspectes
      const attempts = await getLoginAttempts(50);
      detectSuspiciousActivities(attempts);
    } catch (err) {
      toast.error('Erreur lors du chargement des données de sécurité');
    }
  };

  /**
   * Detecter les activites suspectes a partir des tentatives de connexion
   */
  const detectSuspiciousActivities = (attempts: LoginAttempt[]) => {
    const suspicious: SuspiciousActivity[] = [];
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Filtrer les echecs recents
    const recentFailures = attempts.filter(
      (attempt: LoginAttempt) => !attempt.succes && new Date(attempt.dateHeure) > last24h
    );

    if (recentFailures.length > 3) {
      suspicious.push({
        description: `${recentFailures.length} tentatives de connexion echouees en 24h`,
        dateDetection: new Date().toISOString(),
      });
    }

    // Detecter les connexions depuis plusieurs IPs
    const uniqueIPs = new Set(attempts.map((a: LoginAttempt) => a.adresseIP).filter(Boolean));
    if (uniqueIPs.size > 3) {
      suspicious.push({
        description: `Connexions depuis ${uniqueIPs.size} adresses IP differentes`,
        dateDetection: new Date().toISOString(),
      });
    }

    setSuspiciousActivities(suspicious);
  };

  /**
   * Rafraîchir les logs de sécurité
   */
  const fetchSecurityLogs = async () => {
    const logsData = await getSecurityLogs(50);
    setSecurityLogs(logsData);
  };

  /**
   * Ajouter une IP a la blacklist via l'endpoint backend dedie
   */
  const addIPToBlacklist = async (ip: string) => {
    await blockIP(ip, 'Blocage manuel depuis les parametres de securite', 'PERMANENT');
    setIpAutorisees((prev) => [...prev, ip]);
    await loadSecurityData();
  };

  /**
   * Retirer une IP de la liste des IPs autorisees
   */
  const removeIPFromBlacklist = async (ip: string) => {
    if (!settings) {
      throw new Error('Parametres de securite non charges');
    }

    const updatedIPs = ipAutorisees.filter((existingIP: string) => existingIP !== ip);
    await updateSecuritySettings({
      idUtilisateur: userId,
      ipAutorisees: updatedIPs,
    });

    setIpAutorisees(updatedIPs);
    await loadSecurityData();
  };

  /**
   * Valider une adresse IP
   */
  const validateIP = (ip: string): boolean => {
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  };

  /**
   * Ajouter une IP Ã  la blacklist
   */
  const handleAddIP = async () => {
    setIpError(null);

    if (!newIP.trim()) {
      setIpError('L\'adresse IP est requise');
      return;
    }

    if (!validateIP(newIP.trim())) {
      setIpError('Format d\'adresse IP invalide');
      return;
    }

    try {
      await addIPToBlacklist(newIP.trim());
      setNewIP('');
      showAlert('success','IP ajoutée à la blacklist');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'ajout';
      setIpError(message);
      showAlert('error',message);
    }
  };

  /**
   * Retirer une IP de la blacklist
   */
  const handleRemoveIP = async (ip: string) => {
    try {
      await removeIPFromBlacklist(ip);
      showAlert('success', 'IP retiree de la blacklist');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du retrait';
      showAlert('error',message);
    }
  };

  /**
   * RafraÃ®chir les logs
   */
  const handleRefreshLogs = async () => {
    try {
      await fetchSecurityLogs();
      showAlert('success','Logs actualisés');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'actualisation';
      showAlert('error', message);
    }
  };

  /**
   * Formater la date
   */
  const formatDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  };

  /**
   * Obtenir la couleur du type d'action
   */
  const getActionColor = (action: string): string => {
    if (action.toLowerCase().includes('connexion')) return 'text-success';
    if (action.toLowerCase().includes('Echec') || action.toLowerCase().includes('erreur')) {
      return 'text-danger';
    }
    if (action.toLowerCase().includes('modification')) return 'text-accent';
    return 'text-content-muted';
  };

  return (
    <div className={`w-full max-w-4xl ${className}`}>
      {/* Titre */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content">
          Paramètres de sécurité
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Gérez la sécurité de votre compte et surveillez l'activité suspecte
        </p>
      </div>

      {/* Alerte */}
      {alert && (
        <div className="mb-6">
          <SecurityAlert
            {...alert}

          />
        </div>
      )}

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Chargement */}
      {isLoading && (
        <div className="mb-6 text-center">
          <LoadingSpinner size="md" />
        </div>
      )}

      <div className="space-y-6">
        {/* Statistiques de sÃ©curitÃ© */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-accent-light rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-content-muted">Logs totaux</p>
                <p className="text-2xl font-bold text-content">
                  {securityLogs.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-warning-light rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-warning"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-content-muted">Activités suspectes</p>
                <p className="text-2xl font-bold text-content">
                  {suspiciousActivities.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-danger-light rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-danger"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-content-muted">IPs bloquées</p>
                <p className="text-2xl font-bold text-content">
                  {ipAutorisees.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Activités suspectes */}
        {suspiciousActivities.length > 0 && (
          <div className="bg-warning-light border border-warning rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-warning mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-warning">
                  Activités suspectes détectées
                </h4>
                <ul className="mt-2 space-y-1">
                  {suspiciousActivities.map((activity: SuspiciousActivity, index: number) => (
                    <li key={index} className="text-sm text-warning">
                      • {activity.description} ({formatDate(activity.dateDetection)})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Informations détaillées de sécurité */}
        {securityParams && (
          <div className="bg-surface rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-content mb-4">
              Informations de sécurité
            </h3>
            <dl className="space-y-4">
              {/* Statut 2FA */}
              <div className="flex justify-between items-center">
                <dt className="text-sm font-medium text-content-muted">
                  Authentification à deux facteurs
                </dt>
                <dd className="text-sm text-content">
                  {securityParams.deuxFacteursActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success">
                      ✓ Activée ({securityParams.deuxFacteursMethode || 'Non spécifié'})
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-overlay text-content">
                      Désactivée
                    </span>
                  )}
                </dd>
              </div>

              {/* Nombre sessions actives */}
              <div className="flex justify-between items-center">
                <dt className="text-sm font-medium text-content-muted">
                  Sessions actives
                </dt>
                <dd className="text-sm font-semibold text-content">
                  {securityParams.nombreSessionsActives ?? 0} session{(securityParams.nombreSessionsActives ?? 0) > 1 ? 's' : ''}
                </dd>
              </div>

              {/* Dernière connexion */}
              <div className="flex justify-between items-center">
                <dt className="text-sm font-medium text-content-muted">
                  Dernière connexion
                </dt>
                <dd className="text-sm text-content">
                  {securityParams.dateDerniereConnexion ? (
                    <div className="text-right">
                      <div>{formatDate(securityParams.dateDerniereConnexion)}</div>
                      {securityParams.derniereAdresseIP && (
                        <div className="text-xs text-content-subtle">
                          IP: {securityParams.derniereAdresseIP}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-content-subtle">Aucune connexion enregistrée</span>
                  )}
                </dd>
              </div>

              {/* Codes de récupération */}
              <div className="flex justify-between items-center">
                <dt className="text-sm font-medium text-content-muted">
                  Codes de récupération 2FA
                </dt>
                <dd className="text-sm text-content">
                  {securityParams.deuxFacteursActive ? (
                    <span className={(securityParams.nombreBackupCodesDisponibles ?? 0) > 0 ? 'text-success' : 'text-danger'}>
                      {securityParams.nombreBackupCodesDisponibles ?? 0} code{(securityParams.nombreBackupCodesDisponibles ?? 0) > 1 ? 's' : ''} disponible{(securityParams.nombreBackupCodesDisponibles ?? 0) > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="text-content-subtle">2FA non activée</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* Actions rapides */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-content mb-4">
            Actions rapides
          </h3>
          <div className="space-y-3">
            {show2FASection && (
              <button
                onClick={onConfigure2FA}
                className="w-full flex items-center justify-between px-4 py-3 bg-accent-light hover:bg-accent-light rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-accent mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-content">
                    Configurer l'authentification à  deux facteurs
                  </span>
                </div>
                <svg
                  className="w-5 h-5 text-content-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {showPasswordSection && (
              <button
                onClick={onChangePassword}
                className="w-full flex items-center justify-between px-4 py-3 bg-surface-raised hover:bg-surface-overlay rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-content-muted mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-content">
                    Changer le mot de passe
                  </span>
                </div>
                <svg
                  className="w-5 h-5 text-content-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Logs de sécurité */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">
              Logs de sécurité
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleRefreshLogs}
                disabled={isLoading}
                className="px-3 py-1 text-sm font-medium text-accent hover:bg-accent-light rounded-lg transition-colors disabled:opacity-50"
              >
                Actualiser
              </button>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="px-3 py-1 text-sm font-medium text-content-muted hover:bg-surface-overlay rounded-lg transition-colors"
              >
                {showLogs ? 'Masquer' : 'Afficher'}
              </button>
            </div>
          </div>

          {showLogs && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {securityLogs.length === 0 ? (
                <p className="text-sm text-content-subtle text-center py-8">
                  Aucun log de sécurité disponible
                </p>
              ) : (
                securityLogs.slice(0, 20).map((log: SecurityLog) => (
                  <div
                    key={log.id}
                    className="flex items-start justify-between p-3 bg-surface-raised rounded-lg"
                  >
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${getActionColor(log.description)}`}>
                        {log.description}
                      </p>
                      <div className="mt-1 flex items-center gap-4 text-xs text-content-subtle">
                        <span>{formatDate(log.dateCreation)}</span>
                        {log.adresseIP && <span>IP: {log.adresseIP}</span>}
                        {log.userAgent && (
                          <span className="truncate max-w-xs" title={log.userAgent}>
                            {log.userAgent}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Gestion de la blacklist IP */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">
              Blacklist IP
            </h3>
            <button
              onClick={() => setShowBlacklist(!showBlacklist)}
              className="px-3 py-1 text-sm font-medium text-content-muted hover:bg-surface-overlay rounded-lg transition-colors"
            >
              {showBlacklist ? 'Masquer' : 'Afficher'}
            </button>
          </div>

          {showBlacklist && (
            <div className="space-y-4">
              {/* Ajouter une IP */}
              <div>
                <label
                  htmlFor="new-ip"
                  className="block text-sm font-medium text-content-muted mb-2"
                >
                  Ajouter une adresse IP
                </label>
                <div className="flex gap-2">
                  <input
                    id="new-ip"
                    type="text"
                    value={newIP}
                    onChange={(e) => {
                      setNewIP(e.target.value);
                      setIpError(null);
                    }}
                    className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      ipError
                        ? 'border-danger'
                        : 'border-border'
                    } bg-surface text-content`}
                    placeholder="192.168.1.1"
                  />
                  <button
                    onClick={handleAddIP}
                    disabled={isLoading}
                    className="px-4 py-2 bg-danger hover:bg-danger text-content-inverted text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Bloquer
                  </button>
                </div>
                {ipError && <p className="mt-1 text-sm text-danger">{ipError}</p>}
              </div>

              {/* Liste des IPs bloquées */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {ipAutorisees.length === 0 ? (
                  <p className="text-sm text-content-subtle text-center py-4">
                    Aucune IP bloquée
                  </p>
                ) : (
                  ipAutorisees.map((ip: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-danger-light rounded-lg"
                    >
                      <span className="text-sm font-mono text-content">{ip}</span>
                      <button
                        onClick={() => handleRemoveIP(ip)}
                        disabled={isLoading}
                        className="text-sm font-medium text-danger hover:text-danger disabled:opacity-50"
                      >
                        Débloquer
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


